import { z } from "zod";
import { isAddress, zeroAddress, type Address } from "viem";

export const launchpadApi = "https://launchpad-api.merklewood.fun";
export const launchChain = 99901;
export const pairs = [
  { symbol: "HYPE", type: "crypto", address: zeroAddress },
  { symbol: "KNTQ", type: "stock", address: "0xe45a355d9e9cad2ad85f2d03e3ee3102a036acf0" },
  { symbol: "NVDAx", type: "stock", address: "0x11265018bf2d5a99fb71efae73ad670203c1df44" },
] as const;
export const addressSchema = z.custom<Address>(value => typeof value === "string" && isAddress(value));
const nonzero = addressSchema.refine(value => value.toLowerCase() !== zeroAddress);
const count = z.number().int().safe().nonnegative();
const timestamp = count.max(8_640_000_000_000);
const metric = z.number().nonnegative();
const decimal = z.string().regex(/^\d+(\.\d+)?$/);
export const uint = z.string().regex(/^\d+$/);
const hash = z.string().regex(/^0x[\da-f]{64}$/i);
const symbol = z.enum(["HYPE", "KNTQ", "NVDAx"]);
const type = z.enum(["crypto", "stock"]);
const id = z.union([z.literal(0), z.literal(1), z.literal(2)]);
const text = z.string().nullable();
export const meta = z.object({
  chainId: z.literal(launchChain), indexedBlock: count, chainHead: count, lag: count,
  indexerStatus: z.enum(["ready", "syncing", "degraded"]),
  transport: z.enum(["wss-trigger+https-data", "https-data", "https"]),
  wssConnected: z.boolean(), hypeUsdPrice: metric.nullable(),
});
export const envelope = <T extends z.ZodType>(data: T) => z.object({ data, meta });
const pair = z.object({ id, type, symbol, address: addressSchema, enabled: z.boolean() }).refine(row => {
  const known = pairs[row.id];
  return row.type === known.type && row.symbol === known.symbol && row.address.toLowerCase() === known.address;
});
export const pairList = envelope(z.array(pair).refine(rows => new Set(rows.map(row => row.id)).size === rows.length));
export type LaunchPair = z.infer<typeof pair>;

const windowStats = z.object({
  changePct: z.number(), volumeQuote: metric, volumeHype: metric, trades: count, buys: count, sells: count,
});
export const tokenDto = z.object({
  antiSniper: z.object({ enabled: z.boolean(), hookAddress: addressSchema.nullable(), startFeeBps: count, currentFeeBps: count,
    durationSeconds: count, remainingSeconds: count, startsAt: timestamp.nullable(), endsAt: timestamp.nullable(),
    creatorExempt: z.boolean(), maxAdditionalExemptWallets: count,
    decayFeeSplit: z.object({ creatorPct: z.literal(50), protocolPct: z.literal(50) }).nullable(), baseLpFeeBps: z.literal(100) }).optional(),
  address: nonzero, creator: nonzero, name: z.string().min(1), symbol: z.string().min(1),
  pairId: id, pairType: type, pairSymbol: symbol, quoteAddress: addressSchema,
  description: text, logoUrl: text, image: text.default(null), imageUrl: text.default(null), website: text, telegram: text, x: text, metadataUpdatedAt: timestamp.nullable(),
  decimals: z.literal(18), totalSupply: decimal, status: z.literal("live"), launchModel: z.literal("uniswap-v4-one-sided"),
  createdAt: timestamp, createdBlock: count, createdTx: hash, txId: hash, creationTxId: hash, poolId: hash,
  positionTokenId: uint, holders: count,
  priceHype: metric, marketCapHype: metric, fdvHype: metric, priceQuote: metric, marketCapQuote: metric, fdvQuote: metric,
  marketCapBasis: z.literal("HYPE"), quoteBasis: symbol,
  priceUsd: metric.nullable(), marketCapUsd: metric.nullable(), fdvUsd: metric.nullable(),
  priceChange5m: z.number(), priceChange1h: z.number(), priceChange6h: z.number(), priceChange24h: z.number(),
  volume24hHype: metric, trades24h: count, updatedAt: timestamp, updatedBlock: count,
  windows: z.object({ "5m": windowStats, "1h": windowStats, "6h": windowStats, "24h": windowStats }),
}).refine(token => {
  const known = pairs[token.pairId];
  return known.type === token.pairType && known.symbol === token.pairSymbol &&
    token.quoteBasis === token.pairSymbol && known.address === token.quoteAddress.toLowerCase();
});
export type LaunchToken = z.infer<typeof tokenDto>;
export const tokenDetail = (address: Address) => envelope(tokenDto)
  .refine(result => result.data.address.toLowerCase() === address.toLowerCase(), "Token identity mismatch");
export const tokenFeed = envelope(z.array(tokenDto).max(50)).extend({
  pagination: z.object({ page: count, pageSize: count, returned: count, total: count, totalPages: count }),
});
export const intervals = ["1m", "5m", "15m", "1h", "4h", "1d"] as const;
export const chart = envelope(z.array(z.object({
  time: timestamp, price: metric, marketCap: metric, volumeQuote: metric, buys: count, sells: count, trades: count,
})).max(1000)).extend({
  chartType: z.literal("line"), availableMetrics: z.tuple([z.literal("price"), z.literal("marketCap")]),
  display: z.object({ metric: z.literal("marketCap"), autoScale: z.literal(true), includeZero: z.literal(false) }).optional(),
  defaultMetric: z.enum(["price", "marketCap"]), interval: z.enum(intervals), basis: symbol,
  priceBasis: z.literal("HYPE"), marketCapBasis: z.literal("HYPE"), volumeBasis: symbol,
  range: z.object({ from: count, to: count }),
});
export const transactions = envelope(z.array(z.object({
  id: z.string(), txId: hash, blockNumber: count, logIndex: count, timestamp: timestamp, side: z.enum(["buy", "sell"]),
  trader: addressSchema, quoteAmount: decimal, quoteSymbol: symbol, tokenAmount: decimal, price: metric,
  priceQuote: metric.optional(),
}).refine(row => row.id === row.txId + ":" + row.logIndex)).max(50));
export const holders = envelope(z.array(z.object({
  address: nonzero, balanceRaw: uint, balance: decimal, supplyPct: metric, updatedBlock: count,
})).max(50)).extend({
  pagination: z.object({ limit: z.literal(50), offset: z.literal(0), returned: count, total: count, capped: z.literal(true) }),
});

export class LaunchApiError extends Error {
  constructor(readonly status: number, message: string, readonly retryable = false) { super(message); }
}

export async function launchRequest<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  if (import.meta.env.VITE_APP_MODE === "DEMO") throw new Error("Launchpad unavailable in demo");
  const response = await fetch(launchpadApi + "/api" + path, {
    cache: "no-store", credentials: "omit", redirect: "error", headers: { Accept: "application/json" }, ...init,
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = z.object({ error: z.string(), retryable: z.boolean().optional() }).safeParse(body);
    throw new LaunchApiError(response.status, error.success ? error.data.error : "HTTP " + response.status, error.success && error.data.retryable === true);
  }
  const result = schema.safeParse(body);
  if (!result.success) throw new Error("Invalid Launchpad response");
  return result.data;
}

export const logoUrl = (value: string | null) =>
  value && /^\/uploads\/logos\/[\w.-]+$/.test(value) ? launchpadApi + value : undefined;

export function imageUrl(value: string | null | undefined) {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
}

export const metadataDraftHandle = z.object({
  draftId: z.string().min(1), attachKey: z.string().min(1), creator: addressSchema,
  image: z.string().refine(value => Boolean(imageUrl(value))),
});
export type MetadataDraftHandle = z.infer<typeof metadataDraftHandle>;
export const metadataDraftDto = z.object({
  draftId: z.string().min(1), creator: addressSchema, status: z.enum(["pending", "attached"]),
  attachedToken: addressSchema.nullable(), description: text, logoUrl: z.string(),
  image: z.string().refine(value => Boolean(imageUrl(value))), imageUrl: z.string().refine(value => Boolean(imageUrl(value))),
  website: text, telegram: text, x: text, createdAt: timestamp, updatedAt: timestamp, attachedAt: timestamp.nullable(),
});

export function socialUrl(kind: "website" | "telegram" | "x", value: string | null) {
  if (!value) return undefined;
  const host = kind === "x" ? "x.com" : "t.me";
  if (kind !== "website" && /^@[\w]+$/.test(value)) return "https://" + host + "/" + value.slice(1);
  try {
    const url = new URL(value);
    if (url.username || url.password) return undefined;
    if (kind === "website") return ["http:", "https:"].includes(url.protocol) ? url.href : undefined;
    if (url.protocol === "https:" && !url.port && url.pathname.length > 1 &&
        (url.hostname === host || (kind === "x" && url.hostname === "twitter.com"))) return url.href;
  } catch { /* Incomplete or invalid URL. */ }
  return undefined;
}
